import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import { customStyles } from "../Utility/Utility";
import { AddUpdate } from "../../../ApiMethods/ApiMethods";
import { Modal } from "react-bootstrap";
import swal from "sweetalert";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";

const TopicWiseRules = () => {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [postModal, setPostModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [addFormData, setAddFormData] = useState({ TopicName: "", TopicCategory: "", IsActive: 1 });
  const [filters, setFilters] = useState([
    { field: "", operator: "=", value: "", logic: "", columnType: "" },
  ]);
  const [customQuery, setCustomQuery] = useState("");
  const [fieldOptions, setFieldOptions] = useState([]);
  const [categories, setCategories] = useState([]);

  const fetchTopics = async () => {
    try {
      setLoading(true);
      const response = await AddUpdate("NotificationCondition/GetData_NotificationTopics", { IsActive: 1 });
      const parsed = JSON.parse(response?.data?.data || "{}");
      const rows = parsed?.Table || [];
      setTopics(rows);
    } catch (err) {
      console.error("Error fetching topics:", err);
      setError("Failed to load topics");
      setTopics([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchFieldOptions = async () => {
    try {
      const response = await AddUpdate("NotificationSegments/GetFieldDropDown", { Audience: "user" });
      const parsed = JSON.parse(response?.data?.data || "{}");
      setFieldOptions(parsed?.Table || []);
    } catch (err) {
      console.error("Error fetching field options:", err);
      setFieldOptions([]);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await AddUpdate("NotificationCondition/GetData_TopicwiseCategory", { IsActive: 1 });
      const parsed = JSON.parse(response?.data?.data || "{}");
      setCategories(parsed?.Table || []);
    } catch (err) {
      console.error("Error fetching categories:", err);
      setCategories([]);
    }
  };

  useEffect(() => {
    fetchTopics();
    fetchFieldOptions();
    fetchCategories();
  }, []);

  const handleFilterChange = (index, key, value) => {
    const updated = [...filters];
    updated[index][key] = value;
    setFilters(updated);
  };

  const addFilter = () => {
    setFilters([
      ...filters,
      { field: "", operator: "=", value: "", logic: "", columnType: "" },
    ]);
  };

  const deleteFilter = (index) => {
    setFilters((prev) => prev.filter((_, i) => i !== index));
  };

  const buildTopicCondition = () => {
    if (!filters || filters.length === 0) return "";
    const arr = filters.map((filter, index) => {
      const base = {
        ColumnType: String(filter.columnType || ""),
        FieldName: String(filter.field || ""),
        FieldValue: String(filter.value || ""),
        Operator: String(filter.operator || ""),
      };

      // If there are multiple filters, put the logic that connects this filter
      // to the next one on the current item. The UI stores logic on non-first
      // filters, so logic for connection between item0 and item1 is in filters[1].logic.
      if (filters.length > 1 && index < filters.length - 1) {
        const nextLogic = String(filters[index + 1].logic || "AND");
        base.LogicType = nextLogic;
      }

      return base;
    });

    return JSON.stringify(arr);
  };

  const handleSubmit = async () => {
    if (!addFormData.TopicName?.trim()) {
      swal("Validation", "TopicName is required", "warning");
      return;
    }

    setSaving(true);
    try {
      const hasCustom = customQuery && String(customQuery).trim().length > 0;
      const payload = {
        TopicName: addFormData.TopicName,
        TopicCategory: addFormData.TopicCategory,
        CategoryID: categories.find((cat) => cat.TopicwiseCategory === addFormData.TopicCategory)?.CategoryID || null,
        TopicCondition: hasCustom ? "" : buildTopicCondition(),
        CustomQuery: hasCustom ? String(customQuery).trim() : "",
        IsActive: addFormData.IsActive ?? 1,
      };

      const res = await AddUpdate("NotificationCondition/Insert_NotificationTopics", payload);

      let parsedData = {};
      try {
        parsedData = JSON.parse(res?.data?.data || "{}");
      } catch (error) {
        console.error("JSON parse error:", error);
      }

      const message = parsedData?.Table?.[0]?.Message || res?.data?.Message || "";

      if (message && /exist/i.test(message)) {
        swal("Warning", "Topic name already exists", "warning");
      } else if (res?.data?.Message === "Successfully" || /success/i.test(message)) {
        swal("Success", "Topic added", "success");
        setPostModal(false);
        setAddFormData({ TopicName: "", TopicCategory: "", IsActive: 1 });
        setFilters([{ field: "", operator: "=", value: "", logic: "", columnType: "" }]);
        fetchTopics();
        setCustomQuery("");
      } else {
        swal("Error", "Could not add topic", "error");
      }
    } catch (err) {
      console.error("Error inserting topic:", err);
      swal("Error", "Could not add topic", "error");
    } finally {
      setSaving(false);
    }
  };

const handleDelete = async (topicId) => {
  const willDelete = await swal({
    title: "Are you sure?",
    text: "you want,delete This Rule!",
    icon: "warning",
    buttons: true,
    dangerMode: true,
  });

  if (!willDelete) return;

  try {
    await AddUpdate(
      "NotificationCondition/Delete_NotificationTopics",
      { TopicId: topicId }
    );

    swal("Deleted!", "Topic has been deleted.", "success");
    fetchTopics();
  } catch (err) {
    console.error("Error deleting topic:", err);
    swal("Error", "Could not delete topic", "error");
  }
};

  const columns = [
    { name: "Topic Id", selector: (row) => row.TopicId, sortable: true },
    { name: "TopicName", selector: (row) => row.TopicName, sortable: true },
    { name: "TopicCategory", selector: (row) => row.TopicCategory, sortable: true },
    {
      name: "TopicCondition",
      selector: (row) => (row.TopicCondition ? String(row.TopicCondition).slice(0, 80) : ""),
    },
    {
      name: "Action",
      cell: (row) => (
        <div>
          <button className="btn btn-sm btn-danger" onClick={() => handleDelete(row.TopicId)}>
            Delete
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center">
        <h2 className="text-warning fw-bold"> Topics Wise Notification Rules</h2>
        <button className="btn btn-primary" onClick={() => setPostModal(true)}>
        Add Topic Wise Rules
        </button>
      </div>

      <div className="col-12 mt-2">
        <DataTable
          dense
          data={topics}
          columns={columns}
          selectableRowsHighlight
          highlightOnHover
          pagination
          fixedHeader
          customStyles={customStyles}
          persistTableHead
          noDataComponent={loading ? "Loading..." : "There are no data to display"}
        />
        {error && <div className="text-danger mt-2">{error}</div>}
      </div>

      <Modal show={postModal} onHide={() => setPostModal(false)} backdrop="static" size="lg">
        <div className="modal-header">
          <h4 className="modal-title fs-20">Add Topic Wise Rules</h4>
          <button type="button" className="btn-close" aria-label="Close" onClick={() => setPostModal(false)} />
        </div>
        <div className="modal-body p-3">
          <div className="form-group">
            <label>Topic Name</label>
            <input
              type="text"
              className="form-control"
              value={addFormData.TopicName}
              onChange={(e) => setAddFormData({ ...addFormData, TopicName: e.target.value })}
              placeholder="Enter Topic Name"
            />
          </div>

          <div className="form-group mt-2">
            <label>Topic Category</label>
            <select
              className="form-select"
              value={addFormData.TopicCategory}
              onChange={(e) => setAddFormData({ ...addFormData, TopicCategory: e.target.value })}
            >
              <option value="">Select Topic Category</option>
              {categories?.map((item, index) => {
                const value = item.TopicwiseCategory || item.TopicwiseCategory || item.TopicwiseCategory || item.TopicwiseCategory || item.TopicwiseCategory || item.TopicwiseCategory;
                const label = item.TopicwiseCategory || item.DisplayName || item.CategoryName || item.Category || item.Value || item.Name || String(item.Id);
                return (
                  <option key={index} value={value}>
                    {label}
                  </option>
                );
              })}
            </select>
          </div>

          <div className="p-3 rounded mb-3 mt-3" style={{ border: "1px solid #d1d5db", backgroundColor: "#f8fafc" }}>
            <h6 className="fw-semibold mb-3">Filters (Used For Topic Condition)</h6>

            {filters?.map((filter, i) => (
              <div key={i} className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                {i !== 0 && (
                  <div>
                    <select className="form-select" value={filter.logic} onChange={(e) => handleFilterChange(i, "logic", e.target.value)}>
                      <option value="AND">AND</option>
                      <option value="OR">OR</option>
                    </select>
                  </div>
                )}

                <div className="d-flex gap-2">
                  <select className="form-select" style={{ borderColor: "#fdb774" }} value={filter.columnType} onChange={(e) => handleFilterChange(i, "columnType", e.target.value)}>
                    <option value="">Select Column Type</option>
                    <option value="DateDiffDays">DateDiffDays</option>
                    <option value="NormalDate">NormalDate</option>
                    <option value="string">String</option>
                  </select>

                  <select className="form-select" style={{ borderColor: "#fdb774" }} value={filter.field} onChange={(e) => handleFilterChange(i, "field", e.target.value)}>
                    <option value="">Select Field</option>
                    {fieldOptions?.map((item, index) => (
                      <option key={index} value={item.ColumnName}>
                        {item.DisplayName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <select className="form-select" style={{ borderColor: "#fdb774" }} value={filter.operator} onChange={(e) => handleFilterChange(i, "operator", e.target.value)}>
                    <option value="=">Equals</option>
                    <option value="!=">Not Equals</option>
                    <option value=">">Greater Than</option>
                    <option value="<">Less Than</option>
                  </select>
                </div>

                <div>
                  {filter.columnType == "NormalDate" ? (
                    <DatePicker selected={filter.value ? new Date(filter.value) : null} onChange={(date) => {
                        const formattedDate = date ? format(date, "yyyy-MM-dd") : "";
                        handleFilterChange(i, "value", formattedDate);
                      }} placeholderText="Select Date" className="form-control" style={{ borderColor: "#fdb774" }} />
                  ) : (
                    <input type="text" className="form-control" placeholder="Value" style={{ borderColor: "#fdb774" }} value={filter.value} onChange={(e) => handleFilterChange(i, "value", e.target.value)} />
                  )}
                </div>

                <div>
                  <button className="btn btn-outline-danger btn-sm" onClick={() => deleteFilter(i)}>
                    &times;
                  </button>
                </div>
              </div>
            ))}

            <div className="mt-3">
              <button className="btn btn-outline-success btn-sm" onClick={addFilter}>
                + Add Filter
              </button>
            </div>
          </div>

          <div style={{ textAlign: "center", color: "black", marginTop: 8 }}> OR</div>
          <div className="mb-3 mt-2">
            <textarea
              className="form-control"
              style={{ borderColor: "#fdb774" }}
              placeholder="Custom Query"
              value={customQuery}
              onChange={(e) => setCustomQuery(e.target.value)}
              rows={4}
            />
          </div>

          <div className="d-flex justify-content-end">
            <button className="btn btn-primary me-2" disabled={saving} onClick={handleSubmit}>{saving ? "Saving..." : "Save"}</button>
            <button className="btn btn-danger" onClick={() => setPostModal(false)}>Close</button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TopicWiseRules;
